import { z } from "zod";

import { McpToolSchemaHashSchema } from "../../../../schemas/McpServerTool/schemaHash.schema";
import { McpServerToolViewSchema } from "./mcp-server-tool.schemas";

/**
 * Approving one tool.
 *
 * The body names the hash being approved, and the use case refuses when the row
 * has moved on. Without it, an admin reads a contract, a sync lands a new one a
 * second later, and the approval silently lands on text nobody read — which is
 * precisely the tool-poisoning window the whole approval step exists to close.
 */
export const ApproveMcpServerToolBodySchema = z.object({
  /** The `discoveredSchemaHash` the administrator was shown. */
  schemaHash: McpToolSchemaHashSchema
});

export type ApproveMcpServerToolBody = z.infer<typeof ApproveMcpServerToolBodySchema>;

/**
 * One tool an administrator is approving, and the contract they read to decide.
 *
 * 🔴 THE HASH IS PER PAIR, AND THAT IS THE WHOLE REASON THIS IS A LIST OF OBJECTS
 * RATHER THAN A LIST OF IDS. An endpoint taking bare ids would have to source the
 * hash itself — and the only hash it could source is the CURRENT one, which is
 * precisely the value the guard exists to distrust. Approving against a
 * server-sourced hash is not a simpler spelling of this; it is the
 * tool-poisoning window re-opened, with the refusal deleted and nothing on
 * screen to say so.
 */
export const McpToolApprovalRequestSchema = z.object({
  toolId: z.string().uuid(),
  /** The `discoveredSchemaHash` the administrator was shown for THIS tool. */
  schemaHash: McpToolSchemaHashSchema
});

export type McpToolApprovalRequest = z.infer<typeof McpToolApprovalRequestSchema>;

/**
 * The most pairs one call may carry.
 *
 * Discovery puts no ceiling on how many tools a server may advertise — there is
 * no such constant in the module, and a remote server chooses the number — so
 * without a bound here one request is an unbounded statement count. 200 matches
 * `VIBE_ENV_VAR_BULK_DELETE_MAX_IDS`, the house precedent for a bulk body, and
 * sits an order of magnitude above the real fixture this was built for
 * (Circleback advertises 11).
 *
 * debt: a server advertising more than 200 tools cannot be approved in one call,
 *       and this endpoint reports no "there are more" signal — the caller sends
 *       the pairs it holds and reads the outcomes for those.
 *       Ceiling: 200 pairs, so at most 200 reads and 200 conditional updates.
 *       Upgrade trigger: a real MCP server in the fleet advertising >200 tools,
 *       which makes the surface unusable rather than merely chunked.
 */
export const MCP_TOOL_BULK_APPROVE_MAX_PAIRS = 200;

/**
 * Approving several tools of one server in one call.
 *
 * ## Duplicates are REFUSED rather than de-duplicated
 *
 * One response entry per requested pair is the contract, so a repeated `toolId`
 * would have to produce two outcomes for one tool — and the second would report
 * a state the first created rather than anything the administrator asked about.
 * Silently collapsing them is worse: the caller counts its own request against
 * the results and finds one missing with nothing naming which.
 */
export const ApproveMcpServerToolsBodySchema = z.object({
  approvals: z
    .array(McpToolApprovalRequestSchema)
    .min(1)
    .max(MCP_TOOL_BULK_APPROVE_MAX_PAIRS)
    .refine((pairs) => new Set(pairs.map((pair) => pair.toolId)).size === pairs.length, {
      message: "Each toolId may appear at most once."
    })
});

export type ApproveMcpServerToolsBody = z.infer<typeof ApproveMcpServerToolsBodySchema>;

/**
 * Why one pair of a batch was refused, as the domain's OWN code.
 *
 * These are the `code` fields of `McpApprovalIsStaleError`,
 * `McpServerToolNotFoundError` and `McpToolIsRemovedError` — the three refusals
 * `ApproveMcpServerToolUseCase` raises for a tool. They are reused verbatim
 * rather than translated into a second vocabulary, so a caller handling the
 * single-tool endpoint's error codes already handles these, and there is no
 * mapping table to drift.
 *
 * `MCP_SERVER_NOT_FOUND` is deliberately NOT here: a missing server is a
 * property of the request, not of a pair, and it fails the whole call as a 404.
 */
export const McpToolApprovalRefusalReasonSchema = z.enum([
  "MCP_APPROVAL_IS_STALE",
  "MCP_SERVER_TOOL_NOT_FOUND",
  "MCP_TOOL_IS_REMOVED"
]);

export type McpToolApprovalRefusalReason = z.infer<typeof McpToolApprovalRefusalReasonSchema>;

/**
 * What became of ONE requested pair.
 *
 * 🔴 A REFUSED ENTRY CARRIES NO CONTRACT AND NO CURRENT HASH, AND THAT IS A
 * SECURITY PROPERTY RATHER THAN AN OMISSION. The commonest refusal is
 * `MCP_APPROVAL_IS_STALE`, which means the server now advertises something the
 * administrator has not read. Returning that new hash here would let a caller
 * re-submit it immediately — approving, in one automated round trip, a contract
 * no human ever saw. The remedy is to re-read the tool list, which is the only
 * act that puts the new contract in front of a person.
 */
export const McpToolApprovalOutcomeSchema = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("APPROVED"),
    toolId: z.string().uuid(),
    /** The tool as it now stands — the same view the single-tool approve returns. */
    tool: McpServerToolViewSchema
  }),
  z.object({
    outcome: z.literal("REFUSED"),
    toolId: z.string().uuid(),
    reason: McpToolApprovalRefusalReasonSchema
  })
]);

export type McpToolApprovalOutcome = z.infer<typeof McpToolApprovalOutcomeSchema>;

/**
 * One entry per requested pair, in the order they were requested.
 *
 * No counts ride alongside. They are derivable from `results` in one pass, and a
 * stored count is a second source of truth that can disagree with the list it
 * summarises — which is exactly the disagreement a caller would trust over the
 * rows.
 */
export const ApproveMcpServerToolsResultSchema = z.object({
  results: z.array(McpToolApprovalOutcomeSchema)
});

export type ApproveMcpServerToolsResult = z.infer<typeof ApproveMcpServerToolsResultSchema>;

/**
 * Approving the server's instructions. Same read-then-write guard as a tool, for
 * the same reason: instructions reach a model's context.
 */
export const ApproveMcpServerInstructionsBodySchema = z.object({
  /** The `discoveredInstructionsHash` the administrator was shown. */
  instructionsHash: McpToolSchemaHashSchema
});

export type ApproveMcpServerInstructionsBody = z.infer<
  typeof ApproveMcpServerInstructionsBodySchema
>;
